import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import SvgIcon from "@mui/material/SvgIcon";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import SearchIcon from "@mui/icons-material/Search";
import Autocomplete from "@mui/material/Autocomplete";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { arrowDown } from "../../../assets/svgIcons/Svg";
import { filterObjectsUptoCategory, timestamp } from "../../utils/services";
import FroalaEditorComponent from "../froalaHtmlEditor/FroalaEditorComponent";
import { filterSelectTag } from "./CommentLibraryHelper";

const froalaConfig = {
    heightMin: 140,
    heightMax: 360,
    imagePaste: false,
    spellCheck: true,
    linkAutoPrefix: 'https://',
    linkAlwaysBlank: true,
    toolbarButtons: {
        moreText: { buttons: ['bold', 'italic', 'underline', '|', 'fontFamily', 'fontSize', 'textColor', 'backgroundColor'], align: 'left', buttonsVisible: 7 },
        moreParagraph: { buttons: ['|', 'alignLeft', 'alignCenter', 'alignRight', 'formatOLSimple', 'formatUL'], align: 'left', buttonsVisible: 8 },
        moreRich: { buttons: ['|', "insertLink", "outdent", "indent"], align: "left", buttonsVisible: 1 },
        moreMisc: { buttons: ['undo', 'redo', 'selectAll', 'html', 'clearFormatting'], align: 'right', buttonsVisible: 2 }
    },
    toolbarButtonsXS: [['fontFamily', 'fontSize', 'bold', 'italic', 'underline', '|'], ['undo', 'redo']],
    pluginsEnabled: ["wordPaste", "spellChecker", "codeBeautifier", "fontFamily", "fontSize", "colors", "align", "lists", 'paragraphFormat', "link"],
    events: {
    'initialized': function () {
      try {
        if (this.$wp && this.$wp[0]) {
          this.$wp[0].style.height = '340px';
        }
      } catch (e) {}
    }
  }
};

const NarrativeCommentSection = (props) => {
    const { commentData, categoryId, lineId, AllCategoryList, setAllCategoryList, updateTemplate, commentId } = props;
    const commentEditor = useRef();
    const summaryEditor = useRef();
    const dispatch = useDispatch();
    const categories = useSelector((state) => state.templates)?.categoryList;
    const storeData = useSelector((state) => state.commentLibrary);
    const selectedTagIdsList = commentData?.tags?.map((item) => item.id);
    const selectedSummaryTagIdsList = commentData?.summaries?.map((item) => item.id);
    const filteredTagItems = storeData?.clTagList?.filter((item) => selectedTagIdsList?.includes(item.id));
    const filteredSummaryTagItems = storeData?.clSummaryTagList?.filter((item) => selectedSummaryTagIdsList?.includes(item.id));
    const [state, setState] = useState({
        open: false,
        tagOpen: false,
        commentSummaryTag: filteredSummaryTagItems || [],
        commentTag: filteredTagItems || [],
        commentText: { innerHTML: commentData?.comment_text || "", dropdownList: commentData?.comment_dropdowns || [] },
        summaryText: { innerHTML: commentData?.summary_comment_text || "", dropdownList: commentData?.summary_comment_dropdowns || [] },
        commentDropdownList: commentData?.comment_dropdowns || [],
        summaryDropdownList: commentData?.summary_comment_dropdowns || [],
    });
    const { open, tagOpen, commentSummaryTag, commentTag, commentText, summaryText, commentDropdownList, summaryDropdownList } = state;
    const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
    const checkedIcon = <CheckBoxIcon fontSize="small" />;

    useEffect(() => {
        setState((prevState) => ({
            ...prevState,
            commentSummaryTag: filteredSummaryTagItems || [],
            commentTag: filteredTagItems || [],
            commentText: { innerHTML: commentData?.comment_text || "" },
            summaryText: { innerHTML: commentData?.summary_comment_text || "" },
        }));
    }, [commentData]);

    const handleUpdateTemplateData = (data) => {
        const catList = AllCategoryList?.template?.categories;
        const categoryIndex = catList.findIndex((cat) => cat.category_id === categoryId);
        let lineIndex = catList[categoryIndex].lines.findIndex((line) => line.line_id === lineId);
        let commentIndex = catList[categoryIndex].lines[lineIndex].comments.findIndex((comment) => comment.comment_id === commentId);
        catList[categoryIndex].lines[lineIndex].comments[commentIndex] = { ...catList[categoryIndex].lines[lineIndex].comments[commentIndex], ...data, updated_at: timestamp() };
        filterObjectsUptoCategory(catList, categoryId, dispatch, categories);
        updateTemplate("noToast");
        setAllCategoryList((prev) => {
            const newCategoryList = { ...AllCategoryList };
            newCategoryList.template.categories = catList;
            newCategoryList.template.categories.updated_at = timestamp();
            return newCategoryList;
        });
    };

    const handleDropdownList = (name, value) => {
        if (name === "commentText") {
            setState((prevState) => ({ ...prevState, commentDropdownList: value }));
        } else {
            setState((prevState) => ({ ...prevState, summaryDropdownList: value }));
        }
    };

    const handleEditorChange = (name, content) => {
        setState((prevState) => ({ ...prevState, [name]: { innerHTML: content } }));
    };

    const handleChangeTag = (name, value) => {
        setState((prevState) => ({ ...prevState, [name]: value }));
        const modifiedArray = value?.map((obj) => ({
            id: obj.id,
            name: obj?.title || obj?.name,
            color: obj.color,
        }));
        handleUpdateTemplateData(name === "commentTag" ? { tags: modifiedArray } : { summaries: modifiedArray });
    };

    const handleChangetext = (name, value, dropdowns) => {
        setState((prevState) => ({ ...prevState, [name]: value }));
        handleUpdateTemplateData(name === "commentText" ? { comment_text: filterSelectTag(value) } : { summary_comment_text: filterSelectTag(value) });
        if (dropdowns)
            handleUpdateTemplateData(name === "commentText" ? { comment_dropdowns: dropdowns } : { summary_comment_dropdowns: dropdowns });
    };

    const handleChangeDropdown = (event, dropdownUuid, type, commentUUId, commentDisabled) => {
        const selectedValue = event?.target?.value || event;
        const catList = AllCategoryList?.template?.categories;
        const categoryIndex = catList.findIndex((cat) => cat.category_id === storeData?.showClModal?.selectedCategory?.id);
        let lineIndex = catList[categoryIndex]?.lines?.findIndex((line) => line?.line_id === storeData?.showClModal?.selectedLine?.line_id);
        let commentIndex = catList[categoryIndex]?.lines[lineIndex]?.comments.findIndex((comment) => comment?.comment_id === commentId);
        let commentDropdownIndex = catList[categoryIndex]?.lines[lineIndex].comments[commentIndex].comment_dropdowns.findIndex((dropdown) => dropdown.dropdown_uuid === dropdownUuid);
        let summaryDropdownIndex = catList[categoryIndex]?.lines[lineIndex].comments[commentIndex].summary_comment_dropdowns.findIndex((dropdown) => dropdown.dropdown_uuid === dropdownUuid);
        catList[categoryIndex].lines[lineIndex].comments[commentIndex] = { ...catList[categoryIndex]?.lines[lineIndex].comments[commentIndex], updated_at: new Date().toISOString() };
        if (type === "comment") {
            catList[categoryIndex].lines[lineIndex].comments[commentIndex].comment_dropdowns[commentDropdownIndex] = {
                ...catList[categoryIndex].lines[lineIndex].comments[commentIndex].comment_dropdowns[commentDropdownIndex],
                selected_value: selectedValue,
            };
            setState((prevState) => ({ ...prevState, commentText: { ...prevState.commentText, dropdownList: catList[categoryIndex].lines[lineIndex].comments[commentIndex].comment_dropdowns }, commentDropdownList: catList[categoryIndex].lines[lineIndex].comments[commentIndex].comment_dropdowns }));
        } else {
            catList[categoryIndex].lines[lineIndex].comments[commentIndex].summary_comment_dropdowns[summaryDropdownIndex] = {
                ...catList[categoryIndex].lines[lineIndex].comments[commentIndex].summary_comment_dropdowns[summaryDropdownIndex],
                selected_value: selectedValue,
            };
            setState((prevState) => ({ ...prevState, summaryText: { ...prevState.summaryText, dropdownList: catList[categoryIndex].lines[lineIndex].comments[commentIndex].summary_comment_dropdowns }, summaryDropdownList: catList[categoryIndex].lines[lineIndex].comments[commentIndex].summary_comment_dropdowns }));
        }
        filterObjectsUptoCategory(catList, storeData?.showClModal?.selectedCategory?.id, dispatch, categories);
        updateTemplate("noToast");
        setAllCategoryList((prev) => {
            const newCategoryList = { ...AllCategoryList };
            newCategoryList.template.categories = catList;
            newCategoryList.template.categories.updated_at = timestamp();
            return newCategoryList;
        });
    };

    // Rebind event listeners
    const rebindDropdownListeners = () => {
        let commentDropElements = document.querySelectorAll(".comment-dropdown");
        commentDropElements.forEach((element) => {
            const dropType = element.getAttribute("dropType");
            const commentUUId = element.getAttribute("comment_uuid");
            const commentDisabled = element.getAttribute("disabled");
            if (element) {
                element.onchange = (event) => handleChangeDropdown(event, element.id, "comment", commentUUId, commentDisabled);
            }
        });

        let summaryDropElements = document.querySelectorAll(".summary-dropdown");
        summaryDropElements.forEach((element) => {
            const dropType = element.getAttribute("dropType");
            const commentUUId = element.getAttribute("comment_uuid");
            const commentDisabled = element.getAttribute("disabled");
            if (element) {
                element.onchange = (event) => handleChangeDropdown(event, element.id, "summary", commentUUId, commentDisabled);
            }
        });
    };

    rebindDropdownListeners();

    useEffect(() => {
        const summaryEditorData = summaryEditor.current;
        const commentEditorData = commentEditor.current;
        summaryEditorData?.events?.on("contentChanged", () => {
            rebindDropdownListeners();
        });
        commentEditorData?.events?.on("contentChanged", () => {
            rebindDropdownListeners();
        });
        rebindDropdownListeners();
        return () => {
            summaryEditorData?.events?.off("contentChanged");
            commentEditorData?.events?.off("contentChanged");
        };
    }, [AllCategoryList]);

    return (
        <>
            <Typography className="comment-title-text">Comment</Typography>
            <FroalaEditorComponent
                editor={commentEditor}
                id={"commentText"}
                onChange={handleEditorChange}
                editorData={commentText}
                dropdowns={commentDropdownList}
                onBlur={handleChangetext}
                handleDropdownList={handleDropdownList}
                froalaConfig={froalaConfig}
                commentData={commentData}
                type="edit"
                setEditorContent={setState}
                handleChangeDropdown={handleChangeDropdown}
                filterTagList={storeData?.clTagList}
                newOpen={storeData?.showClModal?.open}
            />
            <Typography className="comment-title-text">TAGS</Typography>
            <Autocomplete
                className="autoHover autocompleteCustom"
                id="tags-outlined"
                multiple
                size="small"
                options={storeData?.clTagList || []}
                openOnFocus
                open={tagOpen}
                defaultValue={commentTag || []}
                defaultChecked={commentTag || []}
                value={commentTag}
                disableCloseOnSelect
                getOptionLabel={(option) => option?.title}
                onChange={(e, value) => handleChangeTag("commentTag", value)}
                renderTags={(value, getTagProps) =>
                    value.map((option, index) => <Chip variant="outlined" deleteIcon={<CloseOutlinedIcon />} label={option?.title || option?.name} {...getTagProps({ index })} />)
                }
                renderOption={(props, option, { selected }) => (
                    <li {...props} key={option?.id}>
                        <Checkbox style={{ borderRadius: "50%" }} icon={icon} checkedIcon={checkedIcon} checked={commentTag?.some((tag) => tag.id === option.id)} />
                        <Box
                            sx={{ width: "12px", height: "12px", backgroundColor: option?.color, borderRadius: "50%", display: "flex", justifyContent: "center", alignItems: "center" }}
                            style={{
                                marginRight: 8,
                            }}
                        />
                        {option?.title}
                    </li>
                )}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        variant="outlined"
                        value={commentTag}
                        defaultValue={commentTag}
                        placeholder={commentTag?.length === 0 ? "Add tags" : ""}
                        onClick={() => setState((prevState) => ({ ...prevState, tagOpen: !tagOpen }))}
                        onBlur={() => setState((prevState) => ({ ...prevState, tagOpen: false }))}
                        InputProps={{
                            ...params.InputProps,
                            style: { fontFamily: "Open Sans!important", fontSize: "14px", fontWeight: 400, paddingLeft: commentTag?.length !== 0 ? "0px" : "20px" },
                            endAdornment: (
                                <>
                                    <SearchIcon className={`customSearch ${commentTag?.length !== 0 ? "hidden" : ""}`} style={{ color: "#9b9b9b", marginRight: "8px" }} />
                                    <SvgIcon className={`customArrowDown ${tagOpen ? "arrowActive" : ""}`} viewBox="" onClick={() => setState((prevState) => ({ ...prevState, tagOpen: !tagOpen }))}>
                                        {arrowDown}
                                    </SvgIcon>
                                </>
                            ),
                        }}
                    />
                )}
            />
            <Typography className="comment-title-text">Summary Comment</Typography>
            <FroalaEditorComponent
                editor={summaryEditor}
                onChange={handleEditorChange}
                id={"commentSummary"}
                editorData={summaryText}
                dropdowns={summaryDropdownList}
                onBlur={handleChangetext}
                froalaConfig={froalaConfig}
                commentData={commentData}
                type="edit"
                setEditorContent={setState}
                handleChangeDropdown={handleChangeDropdown}
                filterTagList={storeData?.clTagList}
                newOpen={storeData?.showClModal?.open}
            />
            <Typography className="comment-title-text">Summary Section</Typography>
            <Autocomplete
                className="autoHover autocompleteSummaryCustom"
                id="tags-outlined"
                multiple
                size="small"
                options={storeData?.clSummaryTagList || []}
                disableCloseOnSelect
                getOptionLabel={(option) => option?.title}
                openOnFocus
                open={open}
                defaultChecked={commentSummaryTag || []}
                defaultValue={commentSummaryTag || []}
                value={commentSummaryTag}
                onChange={(e, value) => handleChangeTag("commentSummaryTag", value)}
                renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                        <Chip
                            variant="outlined"
                            deleteIcon={<CloseOutlinedIcon />}
                            label={
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <Box
                                        sx={{
                                            width: "12px",
                                            height: "12px",
                                            backgroundColor: `${option?.color} !important`,
                                            borderRadius: "50%",
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            marginRight: "8px",
                                        }}
                                    />
                                    <div style={{ fontSize: "14px" }}>{option?.name || option?.title}</div>
                                </Box>
                            }
                            {...getTagProps({ index })}
                            sx={{
                                padding: "2px 2px 2px 10px !important",
                                fontSize: "10px !important",
                            }}
                        />
                    ))
                }
                renderOption={(props, option, { selected }) => (
                    <li {...props} key={option?.id}>
                        <Checkbox style={{ borderRadius: "50%" }} icon={icon} checkedIcon={checkedIcon} checked={commentSummaryTag?.some((tag) => tag.id === option.id)} />
                        <Box
                            sx={{ width: "12px", height: "12px", backgroundColor: option?.color, borderRadius: "50%", display: "flex", justifyContent: "center", alignItems: "center" }}
                            style={{
                                marginRight: 8,
                            }}
                        />
                        {option?.title}
                    </li>
                )}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        variant="outlined"
                        defaultValue={commentSummaryTag}
                        value={commentSummaryTag}
                        placeholder={commentSummaryTag?.length === 0 ? "No summary selected" : ""}
                        onClick={() => setState((prevState) => ({ ...prevState, open: !open }))}
                        onBlur={() => setState((prevState) => ({ ...prevState, open: false }))}
                        InputProps={{
                            ...params.InputProps,
                            style: { fontFamily: "Open Sans!important", fontSize: "14px", fontWeight: 400 },
                            endAdornment: (
                                <>
                                    <SvgIcon className={`customArrowDown ${open ? "arrowActive" : ""}`} viewBox="" onClick={() => setState((prevState) => ({ ...prevState, open: !open }))}>
                                        {arrowDown}
                                    </SvgIcon>
                                </>
                            ),
                        }}
                    />
                )}
            />
        </>
    );
};

export default NarrativeCommentSection;